import { ClassicPurchasesRoute } from '@/components/classic-admin/ClassicPurchases';
import { ClassicCouponsRoute } from '@/components/classic-admin/ClassicCoupons';
import { ClassicMailRoute } from '@/components/classic-admin/ClassicMail';
import { ClassicNewsRoute } from '@/components/classic-admin/ClassicNews';
import { ClassicLeaderboardsRoute } from '@/components/classic-admin/ClassicLeaderboards';
import { ClassicLegacyRoute } from '@/routes/classic-legacy';
import { ClassicPlayersRoute, ClassicPlayerRoute, ClassicAuditRoute, ClassicStatusRoute } from "@/routes/classic-admin";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { useAuthStore } from "@/lib/auth-store";
import { LoginRoute } from "@/routes/login";
import { PlayersRoute } from "@/routes/players";
import { PlayersWorkspace } from "@/components/players/PlayersWorkspace";
import { SendMailWorkspace } from "@/components/mail/SendMailWorkspace";
import { PlayerDetailRoute } from "@/routes/player-detail";
import { MailRoute } from "@/routes/mail";
import { BansRoute } from "@/routes/bans";
import { AuditRoute } from "@/routes/audit";
import { AuditWorkspace } from "@/components/audit/AuditWorkspace";
import { AdminsRoute } from "@/routes/admins";
import { AdminsWorkspace } from "@/components/admin-workspace/AdminsWorkspace";
import { DevAccountsRoute } from "@/routes/dev-accounts";
import { DevAccountsWorkspace } from "@/components/admin-workspace/DevAccountsWorkspace";
import { PlayFabLegacyWorkspace } from "@/components/admin-workspace/PlayFabLegacyWorkspace";
import { PlayFabAccountWorkspace } from "@/components/admin-workspace/PlayFabAccountWorkspace";
import { TrialRoute } from "@/routes/trial";
import { TrialWorkspace } from "@/components/trial-workspace/TrialWorkspace";
import { NewsRoute } from "@/routes/news";
import { NewsWorkspace } from "@/components/news-workspace/NewsWorkspace";
import { FlagsRoute } from "@/routes/flags";
import { ModerationWorkspace } from "@/components/moderation/ModerationWorkspace";
import { GameVersionsRoute } from "@/routes/game-versions";
import { GameVersionsWorkspace } from "@/components/game-config/GameVersionsWorkspace";
import { CouponsRoute } from "@/routes/coupons";
import { CouponsWorkspace } from "@/components/game-config/CouponsWorkspace";
import { EventsRoute } from "@/routes/events";
import { EventsWorkspace } from "@/components/events-workspace/EventsWorkspace";
import { BalanceRoute } from "@/routes/balance";
import { BalanceWorkspace } from "@/components/game-config/BalanceWorkspace";
import { RaffleRoute } from "@/routes/raffle";
import { RaffleWorkspace } from "@/components/game-config/RaffleWorkspace";
import { PurchasesRoute } from "@/routes/purchases";
import { PurchasesWorkspace } from "@/components/purchases/PurchasesWorkspace";
import { RecoveryRoute } from "@/routes/recovery";
import { RecoveryWorkspace } from "@/components/recovery-workspace/RecoveryWorkspace";
import { RecoveryDetailRoute } from "@/routes/recovery-detail";
import { RecoveryDetailWorkspace } from "@/components/recovery-workspace/RecoveryDetailWorkspace";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

/** Pages only internal admins may open; the server also rejects their API calls for everyone else. */
function RequireInternal({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.me?.role);
  return <RequireAuth>{role === "internal" ? children : <Navigate to="/" replace />}</RequireAuth>;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const me = useAuthStore((s) => s.me);
  const loaded = useAuthStore((s) => s.loaded);
  if (!loaded) return null;
  if (!me) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App() {
  const refresh = useAuthStore((s) => s.refresh);
  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/games/ec1" element={<RequireAuth><Navigate to="/games/ec1/players" replace /></RequireAuth>} />
          <Route path="/games/ec1/legacy" element={<RequireAuth><ClassicLegacyRoute /></RequireAuth>} />
          <Route path="/games/ec1/legacy/:id" element={<RequireAuth><ClassicLegacyRoute /></RequireAuth>} />
          <Route path="/games/ec1/players" element={<RequireAuth><ClassicPlayersRoute /></RequireAuth>} />
          <Route path="/games/ec1/players/:id" element={<RequireAuth><ClassicPlayerRoute /></RequireAuth>} />
          <Route path="/games/ec1/purchases" element={<RequireInternal><ClassicPurchasesRoute /></RequireInternal>} />
          <Route path="/games/ec1/coupons" element={<RequireAuth><ClassicCouponsRoute /></RequireAuth>} />
          <Route path="/games/ec1/mail" element={<RequireAuth><ClassicMailRoute /></RequireAuth>} />
          <Route path="/games/ec1/news" element={<RequireAuth><ClassicNewsRoute /></RequireAuth>} />
          <Route path="/games/ec1/leaderboards" element={<RequireAuth><ClassicLeaderboardsRoute /></RequireAuth>} />
          <Route path="/games/ec1/audit" element={<RequireAuth><ClassicAuditRoute /></RequireAuth>} />
          <Route path="/games/ec1/status" element={<RequireAuth><ClassicStatusRoute /></RequireAuth>} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/" element={<Navigate to="/accounts" replace />} />
          <Route path="/players" element={<RequireAuth><PlayFabLegacyWorkspace /></RequireAuth>} />
          <Route path="/players/classic" element={<RequireAuth><PlayersRoute /></RequireAuth>} />
          <Route path="/accounts" element={<RequireAuth><PlayersWorkspace /></RequireAuth>} />
          <Route path="/accounts/:playfabId/mail" element={<RequireAuth><SendMailWorkspace /></RequireAuth>} />
          <Route path="/players/:playfabId" element={<RequireAuth><PlayFabAccountWorkspace /></RequireAuth>} />
          <Route path="/players/classic/:playfabId" element={<RequireAuth><PlayerDetailRoute /></RequireAuth>} />
          <Route path="/mail" element={<RequireAuth><MailRoute /></RequireAuth>} />
          <Route path="/trial" element={<RequireAuth><TrialWorkspace /></RequireAuth>} />
          <Route path="/trial/classic" element={<RequireAuth><TrialRoute /></RequireAuth>} />
          <Route path="/news" element={<RequireAuth><NewsWorkspace /></RequireAuth>} />
          <Route path="/news/classic" element={<RequireAuth><NewsRoute /></RequireAuth>} />
          <Route path="/leaderboard" element={<RequireAuth><Navigate to="/trial?tab=leaderboard" replace /></RequireAuth>} />
          <Route path="/flags" element={<RequireAuth><ModerationWorkspace /></RequireAuth>} />
          <Route path="/flags/classic" element={<RequireAuth><FlagsRoute /></RequireAuth>} />
          <Route path="/account-recovery" element={<RequireAuth><RecoveryWorkspace /></RequireAuth>} />
          <Route path="/account-recovery/classic" element={<RequireAuth><RecoveryRoute /></RequireAuth>} />
          <Route path="/account-recovery/:uid" element={<RequireAuth><RecoveryDetailWorkspace /></RequireAuth>} />
          <Route path="/account-recovery/classic/:uid" element={<RequireAuth><RecoveryDetailRoute /></RequireAuth>} />
          <Route path="/game-versions" element={<RequireAuth><GameVersionsWorkspace /></RequireAuth>} />
          <Route path="/game-versions/classic" element={<RequireAuth><GameVersionsRoute /></RequireAuth>} />
          <Route path="/balance" element={<RequireAuth><BalanceWorkspace /></RequireAuth>} />
          <Route path="/balance/classic" element={<RequireAuth><BalanceRoute /></RequireAuth>} />
          <Route path="/coupons" element={<RequireAuth><CouponsWorkspace /></RequireAuth>} />
          <Route path="/coupons/classic" element={<RequireAuth><CouponsRoute /></RequireAuth>} />
          <Route path="/events" element={<RequireAuth><EventsWorkspace /></RequireAuth>} />
          <Route path="/events/classic" element={<RequireAuth><EventsRoute /></RequireAuth>} />
          <Route path="/raffle" element={<RequireAuth><RaffleWorkspace /></RequireAuth>} />
          <Route path="/raffle/classic" element={<RequireAuth><RaffleRoute /></RequireAuth>} />
          <Route path="/purchases" element={<RequireInternal><PurchasesWorkspace /></RequireInternal>} />
          <Route path="/purchases/classic" element={<RequireInternal><PurchasesRoute /></RequireInternal>} />
          <Route path="/bans" element={<RequireAuth><BansRoute /></RequireAuth>} />
          <Route path="/audit" element={<RequireAuth><AuditWorkspace /></RequireAuth>} />
          <Route path="/audit/classic" element={<RequireAuth><AuditRoute /></RequireAuth>} />
          <Route path="/admins" element={<RequireAuth><AdminsWorkspace /></RequireAuth>} />
          <Route path="/admins/classic" element={<RequireAuth><AdminsRoute /></RequireAuth>} />
          <Route path="/dev-accounts" element={<RequireAuth><DevAccountsWorkspace /></RequireAuth>} />
          <Route path="/dev-accounts/classic" element={<RequireAuth><DevAccountsRoute /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
